// aiService.js - Enhanced with AI Analytics tracking and UNIVERSAL Chat Ending with Confirmation
import { queryAI } from './AiChat';
import { sendMessage, escalateToHuman, markAIResolved, createAIAnalytics, updateAIAnalytics, markChatEnded } from './appwriteChat';

/**
 * AI response trigger with analytics tracking and universal chat ending with confirmation
 */
export const triggerAIResponse = async (customerMessage, messageId) => {
  const startTime = Date.now();
  let analyticsDoc = null;
  
  try {
    console.log('🤖 AI Processing:', customerMessage);
    console.log('🔍 Message ID:', messageId);

    // Get current chat state to get conversation and customer info
    const { chatStateManager } = await import('./chatStateManager');
    const currentState = chatStateManager.getState();
    
    if (!currentState.conversation || !currentState.customer) {
      console.error('❌ Missing conversation or customer data');
      return { success: false, error: 'Missing conversation data' };
    }

    const conversation = currentState.conversation;
    const customer = currentState.customer;
    
    // Get message history for context
    const messageHistory = currentState.messages
      .filter(msg => msg.type === 'customer' || msg.type === 'ai')
      .slice(-6)
      .map(msg => ({
        role: msg.type === 'customer' ? 'user' : 'assistant',
        content: msg.content || 'Message content'
      }));

    console.log('📤 Calling AI with context messages:', messageHistory.length);

    // ✅ CREATE INITIAL ANALYTICS DOCUMENT
    try {
      analyticsDoc = await createAIAnalytics({
        conversationId: conversation.$id,
        companyId: customer.companyId,
        userId: customer.$id,
        question: customerMessage,
        timestamp: new Date().toISOString(),
        language: 'de', // Default to German, can be detected
        questionType: detectQuestionType(customerMessage),
        errorOccurred: false
      });
      console.log('📊 Analytics document created:', analyticsDoc.$id);
    } catch (analyticsError) {
      console.error('❌ Error creating analytics:', analyticsError);
      // Continue without analytics - don't fail the AI response
    }

    // ✅ PRE-CHECK: Detect escalation keywords BEFORE calling AI
    const shouldPreEscalate = detectEscalationKeywords(customerMessage, messageHistory);
    
    if (shouldPreEscalate) {
      console.log('🚨 PRE-ESCALATION: Detected clear human request, skipping AI');
      
      // Update analytics for escalation
      if (analyticsDoc) {
        await updateAIAnalytics(analyticsDoc.$id, {
          response: "Verstanden! Ich verbinde Sie sofort mit einem unserer Mitarbeiter. 👥",
          confidence: 'high',
          escalatedToHuman: true,
          escalationReason: 'Customer explicitly requested human agent',
          responseTime: Date.now() - startTime,
          responseLength: 67
        });
      }
      
      // Update conversation booleans immediately
      await escalateToHuman(conversation.$id, 'Customer explicitly requested human agent');
      
      // Send escalation message with correct sender_type
      const escalationMessage = await sendMessage(
        conversation.$id,
        `ai-${customer.companyId}`,
        "Verstanden! Ich verbinde Sie sofort mit einem unserer Mitarbeiter. 👥",
        'ai'
      );

      return {
        success: true,
        aiMessage: escalationMessage,
        requiresEscalation: true,
        analyticsId: analyticsDoc?.$id
      };
    }

    // ✅ NEW: Check for chat end CONFIRMATION responses first
const confirmationResponse = handleChatEndConfirmation(customerMessage, messageHistory);
if (confirmationResponse.isConfirmationResponse) {
      console.log('🔔 Processing chat end confirmation response');
      
  if (confirmationResponse.confirmed) {
        // User confirmed they want to end chat
        console.log('✅ User CONFIRMED chat end - ending chat now');
        
        // Update analytics for confirmed chat end
        if (analyticsDoc) {
          await updateAIAnalytics(analyticsDoc.$id, {
            response: "Vielen Dank für das Gespräch! Auf Wiedersehen! 😊",
            confidence: 'high',
            escalatedToHuman: false,
            escalationReason: null,
            responseTime: Date.now() - startTime,
            responseLength: 45
          });
        }
        
        // Mark conversation as ended
        await markChatEnded(conversation.$id);
        
        // Send final goodbye message
        const goodbyeMessage = await sendMessage(
          conversation.$id,
          `ai-${customer.companyId}`,
          "Vielen Dank für das Gespräch! Auf Wiedersehen! 😊",
          'ai'
        );

        return {
          success: true,
          aiMessage: goodbyeMessage,
          requiresEscalation: false,
          chatEnded: true,
          analyticsId: analyticsDoc?.$id
        };
        
      } else if (confirmationResponse.wantsHuman) {
        // User wants to talk to human instead
        console.log('👤 User wants human agent - escalating');
        
        // Update analytics for escalation
        if (analyticsDoc) {
          await updateAIAnalytics(analyticsDoc.$id, {
            response: "Gerne! Ich verbinde Sie mit einem unserer Mitarbeiter. 👥",
            confidence: 'high',
            escalatedToHuman: true,
            escalationReason: 'Customer chose human agent when asked',
            responseTime: Date.now() - startTime,
            responseLength: 55
          });
        }
        
        // Escalate to human
        await escalateToHuman(conversation.$id, 'Customer chose human agent when asked');
        
        const escalationMessage = await sendMessage(
          conversation.$id,
          `ai-${customer.companyId}`,
          "Gerne! Ich verbinde Sie mit einem unserer Mitarbeiter. 👥",
          'ai'
        );

        return {
          success: true,
          aiMessage: escalationMessage,
          requiresEscalation: true,
          analyticsId: analyticsDoc?.$id
        };
        
      } else {
        // User wants to continue chatting
        console.log('💬 User wants to continue - proceeding with normal AI response');
        // Continue to normal AI processing below
      }
    }

    // ✅ NEW: Check for explicit chat end request (UNIVERSAL - works for any website)
    const wantsToEndChat = detectChatEndKeywords(customerMessage, messageHistory);

    if (wantsToEndChat) {
      console.log('🔔 EXPLICIT chat end request detected - asking for confirmation');
      
      // Update analytics for confirmation request
      if (analyticsDoc) {
        await updateAIAnalytics(analyticsDoc.$id, {
          response: "Möchten Sie den Chat wirklich beenden, oder soll ich Sie mit einem unserer Mitarbeiter verbinden?",
          confidence: 'high',
          escalatedToHuman: false,
          escalationReason: null,
          responseTime: Date.now() - startTime,
          responseLength: 98
        });
      }
      
      // Send confirmation message (UNIVERSAL - works for any business)
      const confirmationMessage = await sendMessage(
        conversation.$id,
        `ai-${customer.companyId}`,
        "Möchten Sie den Chat wirklich beenden, oder soll ich Sie mit einem unserer Mitarbeiter verbinden?\n\nSchreiben Sie:\n• 'beenden' um den Chat zu beenden\n• 'mitarbeiter' um mit einem Menschen zu sprechen\n• oder stellen Sie einfach eine weitere Frage 😊",
        'ai'
      );

      return {
        success: true,
        aiMessage: confirmationMessage,
        requiresEscalation: false,
        awaitingConfirmation: true, // ✅ NEW: Flag for confirmation state
        analyticsId: analyticsDoc?.$id
      };
    }

    // ✅ NORMAL AI PROCESSING - Continue with regular AI response
    const aiResponse = await queryAI(
      customerMessage,
      customer.companyId,
      conversation.$id,
      messageHistory
    );

    console.log('🎯 Raw AI Response:', aiResponse);
    console.log('🔍 AI requiresHumanAgent:', aiResponse.requiresHumanAgent);
    console.log('🔍 AI confidence:', aiResponse.confidence);

    const responseTime = Date.now() - startTime;

    // ✅ UPDATE ANALYTICS WITH AI RESPONSE
    if (analyticsDoc) {
      try {
        await updateAIAnalytics(analyticsDoc.$id, {
          response: aiResponse.answer || "No response generated",
          confidence: aiResponse.confidence || 'medium',
          usedDocuments: aiResponse.usedDocuments || false,
          documentsFound: aiResponse.documentsFound || 0,
          responseTime: responseTime,
          escalatedToHuman: aiResponse.requiresHumanAgent === true,
          escalationReason: aiResponse.requiresHumanAgent ? 'AI determined human agent needed' : null,
          responseLength: (aiResponse.answer || '').length,
          errorOccurred: false
        });
        console.log('📊 Analytics updated with AI response');
      } catch (analyticsError) {
        console.error('❌ Error updating analytics:', analyticsError);
      }
    }

    // ✅ Check if AI wants to escalate to human
    if (aiResponse.requiresHumanAgent === true) {
      console.log('🚨 AI requesting human escalation');
      
      // Update conversation booleans
      await escalateToHuman(conversation.$id, 'AI determined human agent needed');
      
      // Send escalation message with correct sender_type
      const escalationMessage = await sendMessage(
        conversation.$id,
        `ai-${customer.companyId}`,
        aiResponse.answer || "Ich denke, das erfordert menschliche Unterstützung. Lassen Sie mich Sie mit einem unserer Mitarbeiter verbinden! 😊",
        'ai'
      );

      return {
        success: true,
        aiMessage: escalationMessage,
        requiresEscalation: true,
        analyticsId: analyticsDoc?.$id
      };
    }

    // ✅ AI handled the request successfully - send with 'ai' sender_type
    const aiMessage = await sendMessage(
      conversation.$id,
      `ai-${customer.companyId}`,
      aiResponse.answer || "Entschuldigung, ich habe das nicht verstanden.",
      'ai'
    );

    console.log('✅ AI message sent successfully:', aiMessage);

    return {
      success: true,
      aiMessage,
      requiresEscalation: false,
      analyticsId: analyticsDoc?.$id
    };

  } catch (error) {
    console.error('❌ AI Error:', error);
    
    const responseTime = Date.now() - startTime;
    
    // ✅ UPDATE ANALYTICS WITH ERROR
    if (analyticsDoc) {
      try {
        await updateAIAnalytics(analyticsDoc.$id, {
          errorOccurred: true,
          errorMessage: error.message,
          responseTime: responseTime,
          escalatedToHuman: true,
          escalationReason: 'AI system error'
        });
        console.log('📊 Analytics updated with error info');
      } catch (analyticsError) {
        console.error('❌ Error updating analytics with error:', analyticsError);
      }
    }
    
    // ✅ On AI error, escalate to human
    const { chatStateManager } = await import('./chatStateManager');
    const currentState = chatStateManager.getState();
    
    if (currentState.conversation && currentState.customer) {
      await escalateToHuman(currentState.conversation.$id, 'AI system error');
      
      const fallbackMessage = await sendMessage(
        currentState.conversation.$id,
        `ai-${currentState.customer.companyId}`,
        "Es tut mir leid, ich habe gerade ein technisches Problem. Lassen Sie mich Sie mit einem Mitarbeiter verbinden! 😊",
        'ai'
      );

      return {
        success: false,
        error: error.message,
        fallbackMessage,
        requiresEscalation: true,
        analyticsId: analyticsDoc?.$id
      };
    }

    return {
      success: false,
      error: error.message,
      analyticsId: analyticsDoc?.$id
    };
  }
};

/**
 * ✅ UNIVERSAL: Only end chat when user explicitly requests it + confirmation flow
 * REPLACE YOUR EXISTING detectChatEndKeywords FUNCTION WITH THIS
 */
function detectChatEndKeywords(message, messageHistory = []) {
  console.log('🔍 Checking for EXPLICIT chat end request:', message);
  
  // ✅ UNIVERSAL: Only very explicit "I want to end this chat" phrases
  // Works for ANY business/website - no business-specific words
  const explicitEndRequests = [
    // German - Explicit chat ending commands
    'chat beenden', 'chat schließen', 'chat schliessen', 'gespräch beenden',
    'ich will den chat beenden', 'ich möchte den chat beenden',
    'beende den chat', 'schließe den chat', 'chat verlassen',
    'ich bin fertig mit dem chat', 'chat stoppen',
    
    // English - Explicit chat ending commands  
    'end chat', 'close chat', 'exit chat', 'stop chat',
    'i want to end the chat', 'i want to close the chat',
    'end this conversation', 'close this conversation',
    'i\'m done with the chat', 'finish the chat',
    
    // Universal - Simple but explicit (only when used alone or with chat context)
    'ende chat', 'stop chat', 'quit chat', 'exit chat', 'close'
  ];

  const messageLower = message.toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ü/g, 'ue')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .trim();
  
  // ✅ STRICT: Must be explicit chat ending request
  const hasExplicitEndRequest = explicitEndRequests.some(endPhrase => {
    const normalizedPhrase = endPhrase.toLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/ü/g, 'ue')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe');
    
    return messageLower.includes(normalizedPhrase);
  });
  
  // ✅ CRITICAL: NEVER end for casual goodbyes, product questions, or normal conversation
  // "tschüss", "bye", "danke", "kosten", "preis" = just normal conversation, NOT ending chat
  
  console.log('🎯 Explicit end request analysis:', {
    message: message.substring(0, 60) + '...',
    hasExplicitEndRequest,
    decision: hasExplicitEndRequest ? 'TRIGGER_CONFIRMATION' : 'CONTINUE_CHAT'
  });
  
  if (hasExplicitEndRequest) {
    console.log('🔔 EXPLICIT chat end request detected - will trigger confirmation flow');
  } else {
    console.log('✅ No explicit end request - continuing conversation');
  }
  
  return hasExplicitEndRequest;
}

/**
 * ✅ FIXED: Handle chat end confirmation responses
 * REPLACE the existing handleChatEndConfirmation function in your aiService.js with this:
 */
function handleChatEndConfirmation(message, messageHistory) {
  // Check if the previous AI message was asking for confirmation
  const lastAIMessages = messageHistory
    .filter(msg => msg.role === 'assistant')
    .slice(-2); // Check last 2 AI messages to be more robust
  
  const wasAskingConfirmation = lastAIMessages.some(aiMsg => 
    aiMsg.content.includes('Möchten Sie den Chat wirklich beenden') ||
    aiMsg.content.includes('beenden\' um den Chat zu beenden') ||
    aiMsg.content.includes('mitarbeiter\' um mit einem Menschen zu sprechen')
  );
  
  console.log('🔔 Confirmation check:', {
    wasAskingConfirmation,
    lastAIMessages: lastAIMessages.map(m => m.content.substring(0, 50) + '...'),
    customerMessage: message
  });
  
  if (!wasAskingConfirmation) {
    return { isConfirmationResponse: false };
  }
  
  console.log('🔔 IS CONFIRMATION RESPONSE - Analyzing:', message);
  
  const messageLower = message.toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/ü/g, 'ue')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .trim();
  
  // ✅ FIXED: Very specific end confirmation detection
  const endConfirmations = [
    'beenden',           // ✅ This should match!
    'ja beenden',        
    'chat beenden',      
    'yes end',          
    'end it',           
    'ende',
    'ja',               // Simple yes
    'yes'               // Simple yes
  ];
  
  // ✅ FIXED: Much simpler and more reliable confirmation detection
  const confirmedEnd = endConfirmations.some(confirm => {
    const normalizedConfirm = confirm.toLowerCase();
    
    console.log('🔍 Checking confirmation:', {
      messageLower,
      normalizedConfirm,
      exactMatch: messageLower === normalizedConfirm,
      includes: messageLower.includes(normalizedConfirm),
      messageLength: messageLower.length,
      confirmLength: normalizedConfirm.length
    });
    
    // ✅ SIMPLIFIED: Just check if message contains the confirmation word
    // and isn't too long (to avoid false positives)
    if (messageLower === normalizedConfirm) {
      console.log('✅ EXACT MATCH found:', normalizedConfirm);
      return true;
    }
    
    // Also check if message contains the word and is reasonably short
    if (messageLower.includes(normalizedConfirm) && messageLower.length <= 15) {
      console.log('✅ CONTAINS MATCH found:', normalizedConfirm);
      return true;
    }
    
    return false;
  });
  
  // ✅ Check for human request
  const humanRequests = [
    'mitarbeiter', 'mensch', 'agent', 'human', 'person',
    'kundenservice', 'customer service', 'support'
  ];
  
  const wantsHuman = humanRequests.some(human => 
    messageLower.includes(human.toLowerCase())
  );
  
  // ✅ Check for continue signals (questions or negative responses)
  const continueSignals = [
    'nein', 'no', 'weiter', 'continue', 'weitermachen',
    'nicht beenden', 'don\'t end', 'keep going', 'noch eine frage'
  ];
  
  const wantsToContinue = continueSignals.some(cont => 
    messageLower.includes(cont.toLowerCase())
  ) || message.includes('?'); // Questions indicate wanting to continue
  
  const result = {
    isConfirmationResponse: true,
    confirmed: confirmedEnd && !wantsHuman && !wantsToContinue,
    wantsHuman: wantsHuman,
    wantsToContinue: wantsToContinue || (!confirmedEnd && !wantsHuman)
  };
  
  console.log('🔔 DETAILED Confirmation analysis:', {
    messageLower,
    confirmedEnd,
    wantsHuman, 
    wantsToContinue,
    finalDecision: result.confirmed ? 'END_CHAT' : 
                   result.wantsHuman ? 'ESCALATE_HUMAN' : 'CONTINUE_CHAT'
  });
  
  return result;
}

/**
 * ✅ Pre-check for obvious escalation requests
 */
function detectEscalationKeywords(message, messageHistory = []) {
  const escalationPhrases = [
    // Strong escalation phrases
    'escalate to human', 'escalate', 'connect me to human', 'human agent',
    'talk to human', 'speak to human', 'transfer to human',
    'i want to talk to a human', 'i need to speak to a human',
    'put me through to someone', 'get me a real person',
    
    // German equivalents
    'mit mitarbeiter sprechen', 'mitarbeiter verbinden', 'mensch sprechen',
    'ich möchte mit einem mitarbeiter', 'ich will mit mensch sprechen',
    'verbinde mich mit mensch', 'echter mensch', 'menschlicher agent',
    
    // Frustration + human request
    'i don\'t want to talk to you', 'not with you', 'don\'t want ai',
    'ich will nicht mit dir', 'nicht mit dir reden', 'kein ai'
  ];

  const messageLower = message.toLowerCase();
  
  // Check current message
  const hasStrongEscalation = escalationPhrases.some(phrase => 
    messageLower.includes(phrase.toLowerCase())
  );
  
  if (hasStrongEscalation) {
    console.log('🚨 Strong escalation phrase detected:', message);
    return true;
  }
  
  // Check if customer has asked for human multiple times
  const humanKeywords = ['human', 'mitarbeiter', 'mensch', 'agent', 'person'];
  const previousHumanRequests = messageHistory.filter(msg => 
    msg.role === 'user' && humanKeywords.some(keyword => 
      msg.content.toLowerCase().includes(keyword)
    )
  ).length;
  
  const currentHasHumanKeyword = humanKeywords.some(keyword => 
    messageLower.includes(keyword)
  );
  
  if (currentHasHumanKeyword && previousHumanRequests >= 1) {
    console.log('🚨 Customer has requested human multiple times');
    return true;
  }
  
  return false;
}

/**
 * ✅ Detect the type of question being asked
 */
function detectQuestionType(message) {
  const messageLower = message.toLowerCase();
  
  // Question type detection patterns
  if (messageLower.includes('preis') || messageLower.includes('kosten') || messageLower.includes('price')) {
    return 'pricing';
  }
  if (messageLower.includes('hilfe') || messageLower.includes('problem') || messageLower.includes('help')) {
    return 'support';
  }
  if (messageLower.includes('produkt') || messageLower.includes('service') || messageLower.includes('angebot')) {
    return 'product_inquiry';
  }
  if (messageLower.includes('beschwerde') || messageLower.includes('unzufrieden') || messageLower.includes('complaint')) {
    return 'complaint';
  }
  if (messageLower.includes('termin') || messageLower.includes('appointment') || messageLower.includes('meeting')) {
    return 'appointment';
  }
  if (messageLower.includes('rechnung') || messageLower.includes('zahlung') || messageLower.includes('billing')) {
    return 'billing';
  }
  
  return 'general';
}

/**
 * ✅ Function to update analytics with customer feedback
 */
export const updateAnalyticsWithFeedback = async (analyticsId, feedbackData) => {
  if (!analyticsId) return;
  
  try {
    await updateAIAnalytics(analyticsId, {
      customerSatisfaction: feedbackData.rating,
      feedbackComment: feedbackData.comment,
      feedbackSubmittedAt: new Date().toISOString()
    });
    console.log('📊 Analytics updated with customer feedback');
  } catch (error) {
    console.error('❌ Error updating analytics with feedback:', error);
  }
};